<script lang="ts">
  import { ApiError, apiGet } from '../../lib/api';
  import {
    coordQuery,
    parseCoord,
    walkSummary,
    type Coord,
    type WalkLegSummary,
  } from '../../lib/debug-walk';
  import { geoJsonToString, journeyWalksToGeoJson } from '../../lib/geojson';
  import type { JourneyJson } from '../../types/JourneyJson';
  import type { RouteResponse } from '../../types/RouteResponse';
  import MapView from '../map.svelte';

  /**
   * The walk viewer's own store, kept local so this page has no shared-store
   * responsibilities. Every field is a Svelte 5 rune-tracked slot.
   */
  interface DebugWalkState {
    /** Origin coord as typed — parsed on every "Show walk" click. */
    originText: string;
    /** Destination coord as typed — parsed on every "Show walk" click. */
    destText: string;
    /** Most recent /v1/routes response's first journey. */
    journey: JourneyJson | undefined;
    /** Current lifecycle phase surfaced to the button + banner. */
    status: 'idle' | 'loading' | 'ready' | 'error';
    /** Human-readable error message shown when `status === 'error'`. */
    errorMessage: string;
  }

  /** Alexanderplatz (public transit hub, not a residential address). */
  const DEFAULT_ORIGIN = '52.52000,13.41000';
  const DEFAULT_DEST = '52.51080,13.40550';

  const state = $state<DebugWalkState>({
    originText: DEFAULT_ORIGIN,
    destText: DEFAULT_DEST,
    journey: undefined,
    status: 'idle',
    errorMessage: '',
  });

  const summary = $derived(walkSummary(state.journey));

  /** Loads the first journey the API returns for the coord pair into `state`. */
  async function loadJourney(origin: Coord, destination: Coord): Promise<void> {
    const response = await apiGet<RouteResponse>('/v1/routes', coordQuery(origin, destination));
    state.journey = response.journeys[0];
    state.status = 'ready';
  }

  /** Renders an `ApiError` message verbatim; falls back to a generic string otherwise. */
  function reportError(error: unknown): void {
    state.errorMessage = error instanceof ApiError ? error.message : 'Request failed.';
    state.status = 'error';
  }

  /** Flips into the error state with the "bad coord input" message. */
  function reportInvalidInput(): void {
    state.errorMessage = 'Enter both coords as "lat,lon".';
    state.status = 'error';
  }

  /** Wraps `loadJourney` with the error-reporting flip and a loading marker. */
  async function attemptFetch(origin: Coord, destination: Coord): Promise<void> {
    state.status = 'loading';
    try {
      await loadJourney(origin, destination);
    } catch (error: unknown) {
      reportError(error);
    }
  }

  /** Parses both coord fields and dispatches to `attemptFetch` when both are valid. */
  async function fetchWalk(): Promise<void> {
    const origin = parseCoord(state.originText);
    const destination = parseCoord(state.destText);
    if (!origin || !destination) {
      reportInvalidInput();
      return;
    }
    await attemptFetch(origin, destination);
  }

  /** Encodes the current journey's walking legs as a browser-triggered `.geojson` download. */
  function triggerDownload(): void {
    if (!state.journey) return;
    const collection = journeyWalksToGeoJson(state.journey);
    const blob = new Blob([geoJsonToString(collection)], { type: 'application/geo+json' });
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = 'walk.geojson';
    anchor.click();
    URL.revokeObjectURL(blobUrl);
  }

  /** Stable identity for a per-leg summary row in the `{#each}` block. */
  function legKey(legSummary: WalkLegSummary): number {
    return legSummary.index;
  }

  /** Pre-formatted total line — keeps the template to a single `{...}` interpolation. */
  const totalLine = $derived(
    `Total: ${String(summary.totalSeconds)}s · ${String(summary.totalMeters)}m across ${String(summary.legs.length)} leg(s)`,
  );

  /** Pre-formatted per-leg line so the template interpolates one string per row. */
  function legLine(legSummary: WalkLegSummary): string {
    return `Leg ${String(legSummary.index)}: ${String(legSummary.seconds)}s · ${String(legSummary.meters)}m · ${String(legSummary.vertexCount)} verts`;
  }

  /** Pre-formatted "via <entrance>" tail — empty when the leg has no entrance. */
  function entranceTail(legSummary: WalkLegSummary): string {
    return legSummary.entranceLabel ? ` via ${legSummary.entranceLabel}` : '';
  }
</script>

<!-- Developer debug page: /v1/routes response viewer with a walk-focused sidebar
     and a "Download GeoJSON" export button. Not password-gated — mount hash-routed
     via /#/debug/walk so operators can reach it without unlocking the main app. -->
<section class="debug-walk">
  <header class="head">
    <h1>Walk viewer</h1>
    <p class="hint">Enter two coords as <code>lat,lon</code>. Alexanderplatz is preloaded.</p>
  </header>
  <div class="controls">
    <label class="field">
      <span>Origin</span>
      <input type="text" bind:value={state.originText} aria-label="Origin coord" />
    </label>
    <label class="field">
      <span>Destination</span>
      <input type="text" bind:value={state.destText} aria-label="Destination coord" />
    </label>
    <button
      type="button"
      onclick={() => {
        void fetchWalk();
      }}
      disabled={state.status === 'loading'}
    >
      {state.status === 'loading' ? 'Loading…' : 'Show walk'}
    </button>
    <button type="button" onclick={triggerDownload}>Download GeoJSON</button>
  </div>
  {#if state.status === 'error'}
    <p class="error" role="alert">{state.errorMessage}</p>
  {/if}
  <div class="body">
    <aside class="sidebar" aria-label="Walk summary">
      <p class="total">{totalLine}</p>
      <ol class="legs">
        {#each summary.legs as legSummary (legKey(legSummary))}
          <li>{legLine(legSummary)}{entranceTail(legSummary)}</li>
        {/each}
      </ol>
    </aside>
    <div class="map">
      <MapView journey={state.journey} />
    </div>
  </div>
</section>

<style>
  .debug-walk {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    height: 100vh;
    box-sizing: border-box;
    font-family: system-ui, sans-serif;
  }

  .head h1 {
    margin: 0;
    font-size: 18px;
  }

  .hint {
    margin: 4px 0 0;
    font-size: 12px;
    color: #6b6b6b;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: end;
  }

  .field {
    display: flex;
    flex-direction: column;
    font-size: 12px;
  }

  .field input {
    padding: 4px 6px;
    font-family: monospace;
  }

  .error {
    color: #a01313;
    margin: 0;
    font-size: 13px;
  }

  .body {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 8px;
    flex: 1;
    min-height: 0;
  }

  .sidebar {
    overflow: auto;
    background: #f5f5f5;
    padding: 8px;
    border-radius: 4px;
    font-size: 13px;
  }

  .legs {
    margin: 8px 0 0;
    padding-left: 20px;
    font-family: monospace;
    font-size: 12px;
  }

  .map {
    min-height: 0;
  }
</style>
